/**
 * Typed API client.
 *
 * The admin token lives in localStorage rather than a cookie: the backend is a
 * pure bearer-token API with no session state, and this keeps it immune to CSRF.
 * The trade-off is XSS exposure, which is why the app ships no user-generated
 * HTML rendering.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';
const TOKEN_STORAGE_KEY = 'advcontrol.adminToken';

export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setAdminToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token.trim());
}

export function clearAdminToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error.code ?? 'unknown';
    this.details = body?.error.details;
  }

  /** Field-level issues returned by OLX, for inline display on a form. */
  get validation(): Array<{ field: string; title: string }> {
    const details = this.details as { validation?: Array<{ field: string; title: string }> } | undefined;
    return details?.validation ?? [];
  }
}

/** Gateway/proxy failures never reach our Express error middleware, so their
 *  status codes get a human hint instead of a bare "Request failed with 504". */
const GATEWAY_STATUS_HINTS: Record<number, string> = {
  502: 'The server is unreachable (bad gateway). It may be restarting — try again shortly.',
  503: 'The service is temporarily unavailable. Try again shortly.',
  504: 'The server did not respond in time (gateway timeout). The request may still be running in the background — check back in a bit before retrying.',
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAdminToken();

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();

  // The backend always replies with JSON, even on error — a non-JSON body
  // (typically an HTML error page) means a proxy in front of it answered
  // instead, e.g. a reverse-proxy gateway timeout on a slow AI request.
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      const fallback = GATEWAY_STATUS_HINTS[response.status] ?? `The server returned an unexpected response (status ${response.status}).`;
      throw new ApiError(response.status, null, fallback);
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, parsed as ApiErrorBody | null, GATEWAY_STATUS_HINTS[response.status] ?? `Request failed with ${response.status}`);
  }
  return parsed as T;
}

const get = <T,>(path: string) => request<T>(path);
const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
const patch = <T,>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T,>(path: string) => request<T>(path, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Domain types (mirrors of the backend response shapes)
// ---------------------------------------------------------------------------

export interface Account {
  id: number;
  name: string;
  label: string | null;
  clientId: string;
  redirectUri: string;
  contactName: string;
  contactPhone: string | null;
  defaultCityId: number;
  defaultDistrictId: number | null;
  advertiserType: 'private' | 'business';
  defaultCurrency: string;
  enabled: boolean;
  authorized: boolean;
  tokenExpiresAt: string | null;
  refreshExpiresAt: string | null;
  refreshFailures: number;
  lastError: string | null;
  publishedCount: number;
}

export type PublishedStatus = 'pending' | 'active' | 'limited' | 'archived' | 'deleted' | 'failed';

export interface PublishedItem {
  id: number;
  accountId: number;
  account: { id: number; name: string; label: string | null };
  wpProductId: number;
  olxAdvertId: string | null;
  olxCategoryId: number | null;
  olxCategoryPath: string | null;
  title: string;
  priceValue: string | null;
  priceCurrency: string | null;
  status: PublishedStatus;
  olxStatus: string | null;
  olxUrl: string | null;
  publishedAt: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export interface WpProductSummary {
  id: number;
  title: string;
  price: number | null;
  imageUrl: string | null;
  categoryName: string | null;
  modifiedAt: string | null;
  publishedOn: string[];
}

export interface LimitStatus {
  scope: { id: number; categoryId: number; categoryName: string; categoryPath: string } | null;
  rule: { id: number; rawLimit: string; quota: number | null; windowDays: number; unlimited: boolean } | null;
  unlimited: boolean;
  quota: number | null;
  used: number;
  remaining: number | null;
  allowed: boolean;
  nextSlotFreeAt: string | null;
  reason: string;
}

export interface AccountLimitOption {
  accountId: number;
  accountName: string;
  label: string | null;
  enabled: boolean;
  authorized: boolean;
  limit: LimitStatus;
  duplicateAdvertId: string | null;
}

export interface OlxAttributeValue {
  code: string;
  label: string;
}

export interface OlxAdvertPayload {
  title: string;
  description: string;
  category_id: number;
  advertiser_type: 'private' | 'business';
  contact: { name: string; phone?: string };
  location: { city_id: number; district_id?: number };
  attributes: Array<{ code: string; value?: string; values?: string[] }>;
  images?: Array<{ url: string }>;
  price?: { value?: number; currency?: string; negotiable?: boolean; trade?: boolean };
  external_id?: string;
  external_url?: string;
  courier?: boolean;
  auto_extend_enabled?: boolean;
}

export interface PreparedAdvert {
  product: {
    id: number;
    title: string;
    price: number | null;
    images: string[];
    categoryNames: string[];
    sku: string | null;
  };
  payload: OlxAdvertPayload;
  limit: LimitStatus;
  duplicates: {
    onThisAccount: { id: number; olxAdvertId: string | null } | null;
    onOtherAccounts: Array<{ accountId: number; accountName: string; olxAdvertId: string | null; olxUrl: string | null }>;
  };
  blockers: string[];
  agent: {
    provider: string;
    model: string;
    steps: number;
    durationMs: number;
    categoryId: number;
    categoryPath: string;
    reasoning?: string;
    warnings: string[];
    toolTrace: Array<{ step: number; tool: string; args: Record<string, unknown>; ok: boolean }>;
  };
}

export interface DashboardStats {
  accounts: { total: number; authorized: number };
  adverts: Partial<Record<PublishedStatus, number>>;
  categoriesCached: number;
  limitScopes: number;
  failedPublishes: number;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const api = {
  dashboard: () => get<DashboardStats>('/dashboard'),

  accounts: {
    list: () => get<{ accounts: Account[] }>('/accounts').then((r) => r.accounts),
    create: (body: Record<string, unknown>) => post<{ id: number }>('/accounts', body),
    update: (id: number, body: Record<string, unknown>) => patch<{ id: number }>(`/accounts/${id}`, body),
    remove: (id: number) => del<void>(`/accounts/${id}`),
    verify: (id: number) => get<{ ok: boolean; olxUserId: number; name?: string }>(`/accounts/${id}/verify`),
    authorizeUrl: (id: number) => post<{ url: string }>(`/auth/olx/${id}/authorize-url`).then((r) => r.url),
    refresh: (id: number) => post<{ ok: boolean }>(`/auth/olx/${id}/refresh`),
  },

  products: {
    list: (params: { search?: string; limit?: number }) => {
      const query = new URLSearchParams();
      if (params.search) query.set('search', params.search);
      if (params.limit) query.set('limit', String(params.limit));
      return get<{ products: WpProductSummary[] }>(`/products?${query}`).then((r) => r.products);
    },
  },

  adverts: {
    list: (params: { accountId?: number; status?: PublishedStatus; search?: string; limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      if (params.accountId) query.set('accountId', String(params.accountId));
      if (params.status) query.set('status', params.status);
      if (params.search) query.set('search', params.search);
      query.set('limit', String(params.limit ?? 50));
      query.set('offset', String(params.offset ?? 0));
      return get<{ total: number; items: PublishedItem[] }>(`/adverts?${query}`);
    },
    prepare: (body: { accountId: number; wpProductId: number; categoryId?: number; hint?: string }) =>
      post<PreparedAdvert>('/adverts/prepare', body),
    publish: (body: {
      accountId: number;
      wpProductId: number;
      categoryId?: number;
      force?: boolean;
      payload?: OlxAdvertPayload;
    }) =>
      post<{
        item: PublishedItem;
        advert: { id: number; url: string; status: string };
        limitConsumed: boolean;
        warnings: string[];
      }>('/adverts/publish', body),
    updatePrice: (id: number, price: number) => patch<PublishedItem>(`/adverts/${id}/price`, { price }),
    archive: (id: number, options: { isSuccess?: boolean; hardDelete?: boolean }) =>
      post<PublishedItem>(`/adverts/${id}/archive`, options),
    sync: (accountId?: number) => post<{ results: Record<string, unknown> }>('/adverts/sync', { accountId }),
  },

  categories: {
    sync: () => post<{ count: number }>('/categories/sync'),
    suggest: (q: string) =>
      get<{ suggestions: Array<{ id: number; name: string; path: string; isLeaf: boolean | null }> }>(
        `/categories/suggest?q=${encodeURIComponent(q)}`,
      ).then((r) => r.suggestions),
  },

  limits: {
    accountOptions: (categoryId: number, wpProductId?: number) => {
      const query = new URLSearchParams({ categoryId: String(categoryId) });
      if (wpProductId) query.set('wpProductId', String(wpProductId));
      return get<{ options: AccountLimitOption[] }>(`/limits/account-options?${query}`).then((r) => r.options);
    },
    overview: (accountId: number) =>
      get<{
        scopes: Array<{
          scopeId: number;
          categoryPath: string;
          rawLimit: string;
          windowDays: number;
          quota: number | null;
          used: number;
          remaining: number | null;
          matchMethod: string;
        }>;
      }>(`/limits/overview?accountId=${accountId}`).then((r) => r.scopes),
    resolve: () =>
      post<{ rulesUpserted: number; scopesCreated: number; unresolved: Array<{ path: string; reason: string }> }>(
        '/limits/resolve',
      ),
  },

  telegram: {
    poll: () =>
      post<{ reports: Array<{ accountName: string; forwarded: number; error?: string }> }>('/telegram/poll'),
    test: () => post<{ configured: boolean; messageId: number | null }>('/telegram/test'),
  },
};
